/**
 * foundry.js — content module for /foundry spoke page.
 * Source: docs/rebuild/content/website/03-offers.md (Foundry section)
 */

export const foundryHead = {
  eyebrow: 'Flagship \u00b7 build with us',
  heading: 'The Foundry',
  subheading: 'A six-week cohort for founders who want to build their own AI systems.',
};

export const foundryIntro =
  'For founders who want to stay ahead of the AI landscape, or the senior operator they trust to go in their place.';

export const foundryProblem = {
  heading: 'What the Foundry solves',
  body: 'The AI landscape shifts every few weeks. The engineer you hire today ships a system that is outdated by the time they have documented it. The consultant you bring in this quarter teaches practices that are stale next quarter. Hiring for AI is hiring against a moving target, and the target moves faster than onboarding ever could.\n\nThe Foundry is the other answer. You, the founder or a team member, acquire the skills to build what your business needs, extend what you have as AI evolves, and stay fluent with whatever ships next. That is the posture that keeps up. We call it Kaizen: continuous improvement, applied to the AI era.\n\nAfter the six weeks, you and your team keep lifetime access to the Founders Table, the graduate community working through what this landscape keeps throwing at them. Collaboration, early access to tools we build next, priority input on future curriculum. The cohort ends; the community does not.',
  hook: 'Not education. Liberation.',
};

export const foundryHow = {
  heading: 'How it runs',
  intro: 'Six weeks. Five founders per cohort. You walk in with a business that needs systems and the awareness that outsourcing the building will not keep pace. You walk out with multiple production-grade systems shipped into your operation and the capability to keep building as the tools evolve.',
  weeks: [
    {
      label: 'Week 1',
      title: 'Audit',
      body: 'We map the repetition, the manual handoffs, the spreadsheets held together by memory. You leave week 1 with the scope of what you will build in the following five weeks.',
    },
    {
      label: 'Weeks 2 to 5',
      title: 'Teach and build',
      body: 'Weekly live sessions combine teaching (tools, patterns, engineering principles, the shape of the industry) with hands-on work on your own systems. Office hours and async support between sessions. One-on-one guidance throughout.',
    },
    {
      label: 'Week 6',
      title: 'Ship',
      body: 'Every founder ships real systems into production.',
    },
  ],
};

export const foundryWho = {
  heading: 'Who goes through it',
  paragraphs: [
    'The Foundry is designed for founders. Founders feel every bottleneck in the business and have the authority to ship systems that change how the operation runs. When the founder attends, the build happens fastest.',
    'Founders can also send a single senior operator in their place. The ideal proxy is a manager who has been living the pain the system will solve, years deep in the inbox, the compliance workflow, or the bottleneck that keeps recurring. That person already knows what a good fix looks like from the inside. They walk out with the skill to build it, the authority (delegated by you) to ship it, and the fluency to keep improving it as the AI landscape moves.',
    'What we do not recommend: hiring a new AI specialist to send. A specialist has to learn your business before they can build for it. A manager already knows the business; the Foundry teaches them the AI. That sequence ships faster and holds up longer.',
  ],
};

export const foundryOutcomes = {
  heading: 'What you walk out with',
  items: [
    'Multiple production-grade AI systems deployed into your actual operation, not a single prototype. Whatever fits inside six weeks of focused build time.',
    'The technical and operational understanding to maintain and extend those systems without us.',
    'The capability to keep up with AI as the landscape evolves. New models, new tools, new patterns absorbed and applied on the fly, without waiting on a consultant or a new hire.',
    'A peer cohort of four other founders building in parallel. The network persists after the programme ends.',
    'The Founders Table: lifetime membership in the graduate community. First access to new tools we build. Priority input on future curriculum. Lifetime course access as courses ship. Auto-enrolment on graduation.',
    'The mental model to evaluate AI claims, products, and hype without getting hustled.',
  ],
};

export const foundryNot = {
  heading: 'What the Foundry is not',
  items: [
    'Not a generic "learn AI" course. Founder-specific, business-specific, deployment-focused.',
    'Not a lecture programme. Teaching is embedded inside active build work.',
    'Not a "hire our engineers and we will do it for you" engagement. The point is that you build, because outsourcing will not keep up.',
    'Not a gateway to a services engagement. Graduates who never hire us are a success outcome.',
  ],
};

export const foundryCta = {
  label: 'Request a Foundry seat',
};

export const foundryLinks = {
  teamTraining: { href: '/team-training', label: 'For your whole team, not just you' },
  automatedSystems: { href: '/automated-systems', label: 'Want us to build it for you?' },
  systems: { href: '/systems', label: 'AI-native business systems' },
};
